"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { X } from "lucide-react";

import { regionFromPathname } from "@/lib/region";
import { switchRegion } from "@/lib/switch-region";

const DISMISS_KEY = "tf:region-suggest";
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

function isDismissed() {
  return document.cookie.split("; ").some((c) => c === `${DISMISS_KEY}=1`);
}

function dismiss() {
  document.cookie = `${DISMISS_KEY}=1; path=/; max-age=31536000; samesite=lax`;
}

export function RegionBanner() {
  const pathname = usePathname();
  const router = useRouter();
  const region = regionFromPathname(pathname);
  const [show, setShow] = useState(false);
  const visible = show && region === "ie";
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;

    if (region !== "ie" || isDismissed()) {
      queueMicrotask(() => {
        if (active) setShow(false);
      });
      return () => {
        active = false;
      };
    }

    if (
      process.env.NODE_ENV !== "production" &&
      new URLSearchParams(window.location.search).get("region-test") === "uk"
    ) {
      queueMicrotask(() => {
        if (active) setShow(true);
      });
      return () => {
        active = false;
      };
    }

    fetch("/api/geo")
      .then((r) => r.json())
      .then((d: { country?: string | null }) => {
        if (active && d.country === "GB") setShow(true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [region]);

  useEffect(() => {
    if (visible) router.prefetch(`/uk${pathname}`);
  }, [visible, pathname, router]);

  // Set the header's top offset before paint so the banner can't overlap it.
  useIsoLayoutEffect(() => {
    if (!visible) return;
    const el = bannerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const apply = () =>
      root.style.setProperty("--region-banner-h", `${el.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--region-banner-h");
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Region suggestion"
      className="relative z-60 bg-purple-05 text-white"
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-3 px-[clamp(1.25rem,4vw,2.5rem)] py-3 md:flex-row md:items-center md:justify-between md:gap-5">
        <p className="text-[15px] leading-snug md:flex-1">
          It looks like you’re visiting from the United Kingdom. View the UK site
          for local content and pricing.
        </p>
        <div className="flex shrink-0 items-center gap-1 self-end md:gap-2 md:self-auto">
          <form action={switchRegion}>
            <input type="hidden" name="to" value="uk" />
            <input type="hidden" name="path" value={pathname} />
            <button
              type="submit"
              onClick={dismiss}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-[16px] font-semibold text-purple-05 transition-colors hover:bg-purple-00"
            >
              <span
                aria-hidden
                className="inline-block size-4 shrink-0 overflow-hidden rounded-full bg-cover bg-center shadow-[0_0_0_1px_rgba(13,10,44,0.12)]"
                style={{ backgroundImage: "url(/assets/region/uk.svg)" }}
              />
              Visit the UK site
            </button>
          </form>
          <button
            type="button"
            onClick={() => {
              dismiss();
              setShow(false);
            }}
            aria-label="Dismiss"
            className="flex size-8 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="size-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
